import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import { deleteUser, getAvailablePages, listUsers } from "@/api/admin";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserFormDialog } from "@/pages/admin/UserFormDialog";
import { useAuth } from "@/auth/useAuth";

export function AdminUsersPage(): React.ReactElement {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const usersQuery = useQuery({ queryKey: ["admin", "users"], queryFn: listUsers });
  const pagesQuery = useQuery({
    queryKey: ["admin", "available-pages"],
    queryFn: getAvailablePages,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const openCreate = () => {
    setEditingId(null);
    setDialogOpen(true);
  };
  const openEdit = (id: number) => {
    setEditingId(id);
    setDialogOpen(true);
  };

  const handleDelete = async (id: number, username: string) => {
    if (!confirm(`Деактивировать пользователя ${username}?`)) return;
    try {
      await deleteUser(id);
      toast.success("Пользователь деактивирован");
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    } catch {
      toast.error("Не удалось удалить");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Пользователи</h1>
        <Button onClick={openCreate}>
          <Plus className="size-4" />
          Создать
        </Button>
      </div>

      {usersQuery.isLoading && <p className="text-muted-foreground">Загрузка…</p>}
      {usersQuery.isError && <p className="text-destructive">Не удалось загрузить список</p>}

      {usersQuery.data && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Логин</TableHead>
              <TableHead>Роль</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead>Последний вход</TableHead>
              <TableHead className="text-right">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {usersQuery.data.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.id}</TableCell>
                <TableCell className="font-medium">{u.username}</TableCell>
                <TableCell>
                  {u.is_admin ? "администратор" : "пользователь"}
                  {u.sso_managed && (
                    <span
                      className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                      title="Права управляются в SSO (Zitadel)"
                    >
                      SSO
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {u.is_active ? (
                    <span className="text-green-600">активен</span>
                  ) : (
                    <span className="text-muted-foreground">деактивирован</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleString("ru-RU") : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(u.id)}>
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(u.id, u.username)}
                      disabled={u.id === currentUser?.id}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <UserFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingUserId={editingId}
        availablePages={pagesQuery.data ?? []}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["admin", "users"] })}
      />
    </div>
  );
}
